/**
 * The Content Browser (SPEC §5, reference 02): a folder tree (Content ›
 * Roles, Projects, Assets › Models, Resume.pdf), a breadcrumb with a Type
 * filter, and the chosen folder's tiles. Click selects a tile; double-click or
 * Enter opens it: a level through `level.open`, like opening a map asset in
 * Unreal, a folder, or a model (placed in the level).
 *
 * Folders are separate views, as in Unreal and Unity (the Content root shows
 * the folders themselves), and the browser follows the open level into its
 * folder, like Unreal's sync to asset. The Type filter shows everything of
 * one type under the folder, flat, and stays set as the visitor moves between
 * folders, as Unreal's filters do.
 */
import { usePointerKind } from "../hooks/usePointerKind";
import { useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent } from "react";
import { useEditor } from "../EditorContext";
import { useTheme, useWorldSnapshot } from "../hooks/editorHooks";
import { isContextMenuKey, menuPointFor, useContextMenu } from "../ui/ContextMenu";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  EntityTypeIcon,
  FileIcon,
  FolderIcon,
} from "../ui/icons";
import {
  browserFolders,
  contentTypes,
  expandedFor,
  filteredView,
  folderOfLevel,
  folderView,
  hasSubfolders,
  initiallyExpanded,
  visibleFolders,
} from "./contentBrowserModel";
import type {
  AssetTile,
  BrowserFolder,
  ContentType,
  FolderId,
  LevelTile,
} from "./contentBrowserModel";
import { FilterSelect } from "../ui/FilterSelect";
import { startAssetDrag } from "../create/assetDrag";
import { assetPreset } from "../create/createModel";
import { useCreate } from "../create/useCreate";
import { LevelThumbnail } from "./LevelThumbnail";
import { useTourTarget } from "../tour/TourTargets";
import "./contentBrowser.css";
import { useStore } from "../hooks/useStore";

export function ContentBrowserPanel() {
  const { content } = useEditor();
  const { levelId } = useWorldSnapshot();
  const folder = useFollowedFolder(levelId);
  const [type, setType] = useState<ContentType | null>(null);
  const view =
    type === null
      ? folderView(content.index, folder.current, levelId)
      : filteredView(content.index, folder.current, type, levelId);
  const tourTarget = useTourTarget("contentBrowser");
  // Each view starts with its own focus and selection.
  const viewKey = `${folder.current ?? ""}|${type ?? ""}`;

  return (
    <div ref={tourTarget} className="kn-cb" data-testid="content-browser">
      <FolderList active={folder.current} onChoose={folder.choose} />
      <div className="kn-cb__main">
        <Breadcrumb folder={folder.current} onChoose={folder.choose} type={type} onType={setType} />
        {view.kind === "folders" && (
          <FolderGrid key={viewKey} folders={view.folders} onOpen={folder.choose} />
        )}
        {view.kind === "levels" && <LevelGrid key={viewKey} tiles={view.tiles} />}
        {view.kind === "assets" && (
          <AssetGrid
            key={viewKey}
            folders={view.folders}
            tiles={view.tiles}
            onOpenFolder={folder.choose}
          />
        )}
      </div>
    </div>
  );
}

/**
 * The chosen folder: first the open level's (reference 02 opens on Roles),
 * then whatever the visitor picks, until another level opens and the browser
 * follows it into its folder.
 */
function useFollowedFolder(levelId: string | null) {
  const { content } = useEditor();
  const [current, setCurrent] = useState<FolderId | null>(
    () => folderOfLevel(content.index, levelId) ?? browserFolders(content.index)[0]?.id ?? null,
  );
  const [followed, setFollowed] = useState(levelId);

  // Adjusting state while rendering, React's pattern for "reset when a prop changes".
  if (followed !== levelId) {
    setFollowed(levelId);
    const next = folderOfLevel(content.index, levelId);
    if (next) setCurrent(next);
  }

  return { current, choose: setCurrent };
}

/** Content, then each folder on the path; every part but the last goes back up to it. */
function Breadcrumb({
  folder,
  onChoose,
  type,
  onType,
}: {
  folder: FolderId | null;
  onChoose: (folder: FolderId | null) => void;
  type: ContentType | null;
  onType: (type: ContentType | null) => void;
}) {
  const { content } = useEditor();
  const parts = folder === null ? [] : folder.split("/");
  const crumbs = [
    { id: null, label: "Content" },
    ...parts.map((label, i) => ({ id: parts.slice(0, i + 1).join("/"), label })),
  ];

  return (
    <nav className="kn-cb__breadcrumb" aria-label="Breadcrumb">
      {crumbs.map((crumb, i) => (
        <span key={crumb.id ?? ""} className="kn-cb__crumb-part">
          {i > 0 && <span className="kn-cb__separator">/</span>}
          {i < crumbs.length - 1 ? (
            <button
              type="button"
              className="kn-cb__crumb-link"
              onClick={() => {
                onChoose(crumb.id);
              }}
            >
              {crumb.label}
            </button>
          ) : (
            <span className="kn-cb__crumb">{crumb.label}</span>
          )}
        </span>
      ))}
      <span className="kn-cb__spacer" />
      <FilterSelect
        label="Type"
        value={type ?? ""}
        options={[
          { value: "", label: "All types" },
          ...contentTypes(content.index).map((each) => ({ value: each, label: each })),
        ]}
        onChange={(chosen) => {
          onType(chosen === "" ? null : chosen);
        }}
      />
    </nav>
  );
}

// ─── Folders ─────────────────────────────────────────────────────────────

function FolderList({
  active,
  onChoose,
}: {
  active: FolderId | null;
  onChoose: (folder: FolderId | null) => void;
}) {
  const { content, links } = useEditor();
  const folders = browserFolders(content.index);
  const tree = useFolderTree(folders, active);

  return (
    <nav className="kn-cb__folders" aria-label="Folders">
      <button
        type="button"
        className="kn-cb__folder"
        aria-current={active === null}
        onClick={() => {
          onChoose(null);
        }}
      >
        <ChevronDownIcon size={10} className="kn-cb__chevron" />
        <FolderIcon size={13} />
        <span className="kn-cb__folder-name">Content</span>
      </button>
      {visibleFolders(folders, tree.expanded).map((folder) => (
        <div
          key={folder.id}
          className="kn-cb__folder-row"
          // Subfolders indent under their parent, as in Unreal's folder tree.
          style={{ "--kn-depth": folder.depth } as CSSProperties}
        >
          {hasSubfolders(folders, folder.id) && (
            <FoldToggle
              label={folder.label}
              open={tree.expanded.has(folder.id)}
              onToggle={() => {
                tree.toggle(folder.id);
              }}
            />
          )}
          <button
            type="button"
            className="kn-cb__folder kn-cb__folder--child"
            aria-current={active === folder.id}
            onClick={() => {
              onChoose(folder.id);
            }}
          >
            <FolderIcon size={13} className="kn-cb__folder-icon" />
            <span className="kn-cb__folder-name">{folder.label}</span>
            <span className="kn-cb__count">{folder.count}</span>
          </button>
        </div>
      ))}
      {/* Always listed, like the toolbar link: the resume is served even before a real one exists (A-33).
          Listed as Resume.pdf, as the toolbar and menus label it; the download keeps the file's own name. */}
      <a className="kn-cb__folder kn-cb__folder--child" href={links.resume} download>
        <FileIcon size={13} />
        <span className="kn-cb__folder-name">Resume.pdf</span>
        <span className="kn-cb__count">pdf</span>
      </a>
    </nav>
  );
}

/** A folder's expand/collapse chevron, beside its name (a button of its own: buttons don't nest). */
function FoldToggle({
  label,
  open,
  onToggle,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
}) {
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;
  return (
    <button
      type="button"
      className="kn-cb__fold"
      aria-expanded={open}
      aria-label={`${open ? "Collapse" : "Expand"} ${label}`}
      onClick={onToggle}
    >
      <Chevron size={10} />
    </button>
  );
}

/**
 * Which folders are unfolded. Folders with subfolders start folded (Assets),
 * and choosing a folder anywhere (the breadcrumb, a folder tile) unfolds its
 * ancestors, so the tree always shows where the browser is. A view state, as
 * a tree's expansion is in Unreal: not a command, and not kept across visits.
 */
function useFolderTree(folders: readonly BrowserFolder[], active: FolderId | null) {
  const [expanded, setExpanded] = useState(() =>
    expandedFor(initiallyExpanded(folders), active, folders),
  );
  const [shown, setShown] = useState(active);

  // Adjusting state while rendering, React's pattern for "reset when a prop changes".
  if (shown !== active) {
    setShown(active);
    setExpanded(expandedFor(expanded, active, folders));
  }

  const toggle = (id: FolderId) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return { expanded, toggle };
}

// ─── Tiles ───────────────────────────────────────────────────────────────

/**
 * Selection, roving focus and keys shared by every tile grid: arrows move,
 * Enter opens, and the context-menu key asks for a menu.
 */
function useTileGrid<T extends { readonly id: string }>(
  tiles: readonly T[],
  handlers: {
    onOpen: (tile: T) => void;
    onMenu?: (tile: T, at: { x: number; y: number }) => void;
    /**
     * Which tiles a tap opens where the pointer is a finger, as touch file
     * browsers open on a tap (a double-tap is awkward, and not every mobile
     * browser reports one). Tiles it leaves out are only selected. None by default.
     */
    opensOnTap?: (tile: T) => boolean;
  },
) {
  const pointer = usePointerKind();
  const [selected, setSelected] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const refs = useRef(new Map<string, HTMLElement>());

  const tileProps = (tile: T, tabStop: string | undefined) => ({
    ref: (element: HTMLElement | null) => {
      if (element) refs.current.set(tile.id, element);
    },
    role: "option",
    "aria-selected": selected === tile.id,
    tabIndex: tabStop === tile.id ? 0 : -1,
    onClick: () => {
      setSelected(tile.id);
      setFocused(tile.id);
      if (pointer === "coarse" && handlers.opensOnTap?.(tile)) handlers.onOpen(tile);
    },
    onDoubleClick: () => {
      handlers.onOpen(tile);
    },
    onFocus: () => {
      setFocused(tile.id);
    },
    onKeyDown: (event: KeyboardEvent) => {
      if (handlers.onMenu && isContextMenuKey(event)) {
        event.preventDefault();
        handlers.onMenu(tile, menuPointFor(event.currentTarget));
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        handlers.onOpen(tile);
        return;
      }
      const target = tiles[nextIndex(tiles.indexOf(tile), event.key, tiles.length)];
      if (!target) return;
      event.preventDefault();
      setFocused(target.id);
      setSelected(target.id);
      refs.current.get(target.id)?.focus();
    },
  });

  return { focused, setSelected, tileProps };
}

/** The Content root: one tile per folder, opened like a folder in Unreal's asset view. */
function FolderGrid({
  folders,
  onOpen,
}: {
  folders: readonly BrowserFolder[];
  onOpen: (folder: FolderId) => void;
}) {
  const grid = useTileGrid(folders, {
    onOpen: (folder) => {
      onOpen(folder.id);
    },
    opensOnTap: () => true,
  });
  const tabStop = grid.focused ?? folders[0]?.id;

  return (
    <div className="kn-cb__grid" role="listbox" aria-label="Content">
      {folders.map((folder) => (
        <div key={folder.id} {...grid.tileProps(folder, tabStop)} className="kn-cb__tile">
          <FolderTileBody folder={folder} />
        </div>
      ))}
    </div>
  );
}

/** A folder tile's face: the folder glyph in place of a thumbnail, its name and what it holds. */
function FolderTileBody({ folder }: { folder: BrowserFolder }) {
  const unit = folder.holds === "levels" ? "level" : "asset";
  return (
    <>
      <span className="kn-cb__thumb kn-cb__thumb--folder" aria-hidden="true">
        <FolderIcon size={32} />
      </span>
      <span className="kn-cb__name">{folder.label}</span>
      <span className="kn-cb__subtitle">
        {folder.count} {folder.count === 1 ? unit : `${unit}s`}
      </span>
    </>
  );
}

function LevelGrid({ tiles }: { tiles: readonly LevelTile[] }) {
  const { dispatch, content, links, savedLevels } = useEditor();
  const menu = useContextMenu();
  // Rendered thumbnails come in both theme modes; show the one matching the editor.
  const { mode } = useTheme();
  // Levels with edits saved in this browser: marked, and resettable.
  const modified = useStore(savedLevels);

  const open = (tile: LevelTile) => {
    void dispatch({ type: "level.open", payload: { id: tile.id }, origin: "contentBrowser" });
  };

  /** A tile's context menu: open it, copy the address that opens it, or drop its saved edits. */
  const openMenu = (tile: LevelTile, at: { x: number; y: number }) => {
    grid.setSelected(tile.id);
    menu.open(at, tile.name, [
      {
        label: "Open",
        run: () => {
          open(tile);
        },
      },
      {
        label: "Copy link",
        run: () => {
          const url = new URL(links.level(tile.id), window.location.href).href;
          void navigator.clipboard.writeText(url);
        },
      },
      {
        label: "Reset to Default",
        disabled: !modified.has(tile.id),
        run: () => {
          void dispatch({
            type: "level.reset",
            payload: { id: tile.id },
            origin: "contentBrowser",
          });
        },
      },
    ]);
  };

  const grid = useTileGrid(tiles, { onOpen: open, onMenu: openMenu, opensOnTap: () => true });
  const tabStop = grid.focused ?? tiles.find((tile) => tile.open)?.id ?? tiles[0]?.id;

  return (
    <>
      <div className="kn-cb__grid" role="listbox" aria-label="Levels">
        {tiles.map((tile) => (
          <div
            key={tile.id}
            {...grid.tileProps(tile, tabStop)}
            aria-label={`${tile.name}${tile.open ? " (open)" : ""}`}
            className="kn-cb__tile"
            title={tile.tooltip}
            onContextMenu={(event) => {
              event.preventDefault();
              openMenu(tile, { x: event.clientX, y: event.clientY });
            }}
          >
            <span className="kn-cb__thumb">
              {tile.thumbnail ? (
                <img src={content.url(tile.thumbnail[mode])} alt="" />
              ) : (
                <LevelThumbnail hueStep={tile.hueStep} />
              )}
              {modified.has(tile.id) && (
                <span
                  className="kn-cb__modified"
                  role="img"
                  aria-label="Modified"
                  title="Modified"
                />
              )}
              {tile.open && (
                <span className="kn-cb__badge">
                  <span className="kn-cb__badge-dot" aria-hidden="true" />
                  OPEN
                </span>
              )}
            </span>
            <span className="kn-cb__name">{tile.name}</span>
            {tile.position && <span className="kn-cb__position">{tile.position}</span>}
            <span className="kn-cb__subtitle" data-placeholder={tile.subtitlePlaceholder}>
              {tile.subtitle}
            </span>
          </div>
        ))}
      </div>
      {/* Beside the listbox, not inside it: a menu isn't a listbox option. */}
      {menu.element}
    </>
  );
}

/**
 * An asset folder: its subfolders, then its models, to place in the level by
 * dragging onto the viewport (as in Unreal), or with Enter, double-click or
 * the menu, which put them at the camera's focus point.
 */
function AssetGrid({
  folders,
  tiles,
  onOpenFolder,
}: {
  folders: readonly BrowserFolder[];
  tiles: readonly AssetTile[];
  onOpenFolder: (folder: FolderId) => void;
}) {
  const { content, placement } = useEditor();
  const create = useCreate("contentBrowser");
  const menu = useContextMenu();
  const items = tiles.map((tile) => ({ id: tile.path, tile }));
  const entries: readonly ({ id: string } & ({ folder: BrowserFolder } | { tile: AssetTile }))[] = [
    ...folders.map((folder) => ({ id: folder.id, folder })),
    ...items,
  ];

  const place = (tile: AssetTile) => {
    create(assetPreset(tile));
  };
  const openMenu = (tile: AssetTile, at: { x: number; y: number }) => {
    menu.open(at, tile.name, [
      {
        label: "Place in level",
        run: () => {
          place(tile);
        },
      },
    ]);
  };
  const grid = useTileGrid(entries, {
    onOpen: (entry) => {
      if ("folder" in entry) onOpenFolder(entry.folder.id);
      else place(entry.tile);
    },
    // Folders open on a tap; an asset only selects, since opening one places it in the level.
    opensOnTap: (entry) => "folder" in entry,
    onMenu: (entry, at) => {
      if ("tile" in entry) openMenu(entry.tile, at);
    },
  });
  const tabStop = grid.focused ?? entries[0]?.id;

  return (
    <>
      <div className="kn-cb__grid" role="listbox" aria-label="Assets">
        {folders.map((folder) => (
          <div
            key={folder.id}
            {...grid.tileProps({ id: folder.id, folder }, tabStop)}
            aria-label={folder.label}
            className="kn-cb__tile"
          >
            <FolderTileBody folder={folder} />
          </div>
        ))}
        {items.map((item) => (
          <div
            key={item.id}
            {...grid.tileProps(item, tabStop)}
            aria-label={item.tile.name}
            className="kn-cb__tile"
            title={item.tile.path}
            draggable
            onDragStart={(event) => {
              startAssetDrag(event.dataTransfer, item.tile.path);
              // The viewport can't read the drag's data until the drop, so the ghost learns it here.
              const asset = content.index.assets.find((entry) => entry.path === item.tile.path);
              if (asset) placement.begin(asset);
            }}
            onDragEnd={() => {
              placement.end();
            }}
            onContextMenu={(event) => {
              event.preventDefault();
              openMenu(item.tile, { x: event.clientX, y: event.clientY });
            }}
          >
            <span className="kn-cb__thumb kn-cb__thumb--folder" aria-hidden="true">
              {item.tile.thumbnail ? (
                <img className="kn-cb__asset-image" src={content.url(item.tile.thumbnail)} alt="" />
              ) : (
                <EntityTypeIcon name="mesh" size={28} />
              )}
            </span>
            <span className="kn-cb__name">{item.tile.name}</span>
            <span className="kn-cb__subtitle">{item.tile.detail}</span>
          </div>
        ))}
      </div>
      {menu.element}
    </>
  );
}

/** Tiles read left to right in one sequence, like Unreal's asset grid with a flat selection. */
function nextIndex(index: number, key: string, count: number): number {
  switch (key) {
    case "ArrowRight":
    case "ArrowDown":
      return Math.min(count - 1, index + 1);
    case "ArrowLeft":
    case "ArrowUp":
      return Math.max(0, index - 1);
    case "Home":
      return 0;
    case "End":
      return count - 1;
    default:
      return -1;
  }
}
